import { type DocumentNode, gql, useQuery, useReactiveVar } from '@apollo/client';
import { ChevronRight, ExpandMore, People, Person, Search, Settings } from '@mui/icons-material';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ReorderIcon from '@mui/icons-material/Reorder';
import TheatersIcon from '@mui/icons-material/Theaters';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { Link, useLocation } from '@tanstack/react-router';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import React, { type ReactNode, useContext, useState } from 'react';
import getVersion from 'src/api/admin/version';
import CustomLinearProgress from 'src/components/loading/CustomLinearProgress';
import viewStateVar from 'src/store/viewState';
import './DrawerStyles.css';
import styles from 'src/Administration/Drawer/DrawerStyles';
import AuthContext from 'src/context/AuthContext';

// Define the valid systemadmin route paths as a union type
type SystemAdminRoute =
  | '/systemadmin/users'
  | '/systemadmin/groups'
  | '/systemadmin/system-settings'
  | '/systemadmin/metadata/field-definitions'
  | '/systemadmin/metadata/field-maps'
  | '/systemadmin/metadata/sorting-and-filtering'
  | '/systemadmin/page-content/library/grid'
  | '/systemadmin/page-content/library/list'
  | '/systemadmin/page-content/media-page'
  | '/systemadmin/page-content/gallery-page';

interface MenuItem {
  to: SystemAdminRoute;
  label: string;
  icon: ReactNode;
}

interface VersionData {
  version: string;
}

interface AllAssetsData {
  allAssets: {
    totalCount: number;
  };
}

interface AllAssetsQuery {
  query: {
    allAssets: {
      totalCount: boolean;
    };
  };
}

export default function Drawer(): React.ReactElement {
  const context = useContext(AuthContext);
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);
  const location = useLocation();
  const pathname = location.pathname;
  const [expandedSections, setExpandedSections] = useState<string[]>(['metadata', 'pageContent', 'library']);

  const toggleSection = (section: string): void => {
    setExpandedSections((prev) => (prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]));
  };

  const { data: versionData } = useQuery<VersionData>(getVersion, {
    fetchPolicy: 'cache-and-network',
  });

  const allAssetsQuery: AllAssetsQuery = {
    query: {
      allAssets: {
        totalCount: true,
      },
    },
  };

  const buildQuery = (): DocumentNode => {
    const builtAllAssetQuery = jsonToGraphQLQuery(allAssetsQuery, { pretty: true });
    return gql(builtAllAssetQuery);
  };

  const { data: totalCountData } = useQuery<AllAssetsData>(buildQuery(), {
    fetchPolicy: 'cache-and-network',
  });

  const getButtonColor = (menuItem: MenuItem): 'primary' | undefined => {
    return pathname.includes(menuItem.to) ? 'primary' : undefined;
  };

  const getButtonSx = (menuItem: MenuItem): { color?: string } => {
    return pathname.includes(menuItem.to) ? {} : { color: 'text.primary' };
  };

  return (
    <div className={classes.drawerWrapper}>
      {(viewState.signedInUserRole === 'ADM' ? ONLY_ADMIN_MAIN_LIST : ADMIN_MANAGER_MAIN_LIST).map((menuItem) => (
        <div key={menuItem.label}>
          <Link to={menuItem.to} style={{ textDecoration: 'none' }}>
            <Button
              startIcon={menuItem.icon}
              className={classes.mainIconButton}
              color={getButtonColor(menuItem)}
              sx={getButtonSx(menuItem)}
            >
              {menuItem.label}
            </Button>
          </Link>
        </div>
      ))}
      {viewState.signedInUserRole === 'ADM' && (
        <div className={classes.treeViewWrapper}>
          {/* Metadata Section */}
          <button
            type="button"
            className={classes.sectionHeader}
            onClick={() => toggleSection('metadata')}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px 0',
              background: 'none',
              border: 'none',
              font: 'inherit',
              color: 'inherit',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {expandedSections.includes('metadata') ? <ExpandMore /> : <ChevronRight />}
            <span className={classes.treeItemWrapper}>Metadata</span>
          </button>
          {expandedSections.includes('metadata') && (
            <div className={classes.sectionContent}>
              {ONLY_ADMIN_METADATA_LIST.map((menuItem) => (
                <Link to={menuItem.to} key={menuItem.label} style={{ textDecoration: 'none' }}>
                  <Button
                    startIcon={menuItem.icon}
                    className={classes.metadataIconButton}
                    color={getButtonColor(menuItem)}
                    sx={getButtonSx(menuItem)}
                  >
                    {menuItem.label}
                  </Button>
                </Link>
              ))}
            </div>
          )}

          {/* Page Content Section */}
          <button
            type="button"
            className={classes.sectionHeader}
            onClick={() => toggleSection('pageContent')}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              padding: '6px 0',
              background: 'none',
              border: 'none',
              font: 'inherit',
              color: 'inherit',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {expandedSections.includes('pageContent') ? <ExpandMore /> : <ChevronRight />}
            <span className={classes.treeItemWrapper}>Page Content</span>
          </button>
          {expandedSections.includes('pageContent') && (
            <div className={classes.sectionContent}>
              {/* Library Sub-section */}
              <button
                type="button"
                onClick={() => toggleSection('library')}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 0',
                  marginLeft: '16px',
                  background: 'none',
                  border: 'none',
                  font: 'inherit',
                  color: 'inherit',
                  width: 'calc(100% - 16px)',
                  textAlign: 'left',
                }}
              >
                {expandedSections.includes('library') ? (
                  <ExpandMore fontSize="small" />
                ) : (
                  <ChevronRight fontSize="small" />
                )}
                <span className={classes.libraryItemWrapper}>Library</span>
              </button>
              {expandedSections.includes('library') && (
                <div style={{ marginLeft: '32px' }}>
                  {ONLY_ADMIN_LIBRARY_LIST.map((menuItem) => (
                    <div key={menuItem.label}>
                      <Link to={menuItem.to} style={{ textDecoration: 'none' }}>
                        <Button
                          startIcon={menuItem.icon}
                          className={classes.pageContentIconButton}
                          color={getButtonColor(menuItem)}
                          sx={getButtonSx(menuItem)}
                        >
                          {menuItem.label}
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              )}
              {/* Media Page items */}
              {ONLY_ADMIN_MEDIA_PAGE_LIST.map((menuItem) => (
                <div key={menuItem.label} style={{ marginLeft: '16px' }}>
                  <Link to={menuItem.to} style={{ textDecoration: 'none' }}>
                    <Button
                      startIcon={menuItem.icon}
                      className={classes.mediaPageIconButton}
                      color={getButtonColor(menuItem)}
                      sx={getButtonSx(menuItem)}
                    >
                      {menuItem.label}
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className={classes.infoParent}>
        {versionData ? (
          <Typography>Akomi: v{versionData.version}</Typography>
        ) : (
          <Typography>
            <CustomLinearProgress />
          </Typography>
        )}
        <Typography className={classes.infoItem}>
          Total: {totalCountData?.allAssets?.totalCount ? `${totalCountData?.allAssets?.totalCount} Items ` : '??'}
        </Typography>
        <Typography className={classes.infoItem}>
          License Status: {context.validLicense ? 'Valid' : <span className={classes.red}>Invalid</span>}
        </Typography>
      </div>
    </div>
  );
}

const ONLY_ADMIN_MAIN_LIST: MenuItem[] = [
  {
    to: '/systemadmin/users',
    label: 'Users',
    icon: <Person />,
  },
  {
    to: '/systemadmin/groups',
    label: 'Groups',
    icon: <People />,
  },
  {
    to: '/systemadmin/system-settings',
    label: 'System Settings',
    icon: <Settings />,
  },
];

const ADMIN_MANAGER_MAIN_LIST: MenuItem[] = [
  {
    to: '/systemadmin/users',
    label: 'Users',
    icon: <Person />,
  },
  {
    to: '/systemadmin/groups',
    label: 'Groups',
    icon: <People />,
  },
];

const ONLY_ADMIN_METADATA_LIST: MenuItem[] = [
  {
    to: '/systemadmin/metadata/field-definitions',
    label: 'Field Definitions',
    icon: <ChecklistRtlIcon />,
  },
  {
    to: '/systemadmin/metadata/field-maps',
    label: 'Field Maps',
    icon: <ListAltIcon />,
  },
];

const ONLY_ADMIN_LIBRARY_LIST: MenuItem[] = [
  {
    to: '/systemadmin/page-content/library/grid',
    label: 'Grid',
    icon: <ViewModuleIcon />,
  },
  {
    to: '/systemadmin/page-content/library/list',
    label: 'List',
    icon: <ReorderIcon />,
  },
  {
    to: '/systemadmin/metadata/sorting-and-filtering',
    label: 'Sorting & Filtering',
    icon: <Search />,
  },
];

const ONLY_ADMIN_MEDIA_PAGE_LIST: MenuItem[] = [
  {
    to: '/systemadmin/page-content/media-page',
    label: 'Media Page',
    icon: <TheatersIcon />,
  },
  {
    to: '/systemadmin/page-content/gallery-page',
    label: 'Gallery Page',
    icon: <TheatersIcon />,
  },
];
